import React, { useEffect, useMemo, useRef, useState } from 'react'
import Plate from "../../Plate";
import TimeIcon from "../../../assets/icon/TimeIcon";
import InfoIcon from "../../../assets/icon/InfoIcon";

import styles from './index.module.scss'
import DropDown from "../../DropDown";
import Divider from "../../Divider";
import timeToDHMFormat from "../../../utils/time";

type TimeUnit = 'days' | 'hours' | 'minutes'

interface TimeTrackerPickerProps {
    getSpendTime: (spendTime: number) => void
    spendTime: number | null
    onlyIcon?: boolean
    dropdownPosition?: 'left' | 'right' | 'side-right'
}

const HOURS_IN_DAY = 24
const MINUTES_IN_HOUR = 60
const MINUTES_IN_DAY = HOURS_IN_DAY * MINUTES_IN_HOUR

const TIME_UNIT_OPTIONS: { id: TimeUnit, title: string }[] = [
    { id: 'days', title: 'Дни (д)' },
    { id: 'hours', title: 'Часы (ч)' },
    { id: 'minutes', title: 'Минуты (м)' },
]

function roundTo(value: number, precision = 4) {
    const factor = 10 ** precision

    return Math.round(value * factor) / factor
}

function isWhole(value: number) {
    return Math.abs(value - Math.round(value)) < 0.000001
}

function formatNumber(value: number) {
    const rounded = roundTo(value, 4)

    if (isWhole(rounded)) {
        return String(Math.round(rounded))
    }

    return rounded.toFixed(4).replace(/\.?0+$/, '')
}

function toHours(value: number, unit: TimeUnit) {
    if (unit === 'days') {
        return value * HOURS_IN_DAY
    }

    if (unit === 'minutes') {
        return value / MINUTES_IN_HOUR
    }

    return value
}

function fromHours(value: number, unit: TimeUnit) {
    if (unit === 'days') {
        return value / HOURS_IN_DAY
    }

    if (unit === 'minutes') {
        return value * MINUTES_IN_HOUR
    }

    return value
}

function getDisplayState(spendTime: number, preferredUnit?: TimeUnit) {
    if (preferredUnit) {
        const preferredValue = roundTo(fromHours(spendTime, preferredUnit))

        if (preferredUnit === 'hours' || isWhole(preferredValue)) {
            return {
                inputValue: formatNumber(preferredValue),
                unit: preferredUnit,
            }
        }
    }

    const totalMinutes = roundTo(spendTime * MINUTES_IN_HOUR)

    if (isWhole(totalMinutes / MINUTES_IN_DAY)) {
        return {
            inputValue: formatNumber(totalMinutes / MINUTES_IN_DAY),
            unit: 'days' as TimeUnit,
        }
    }

    if (isWhole(totalMinutes / MINUTES_IN_HOUR)) {
        return {
            inputValue: formatNumber(totalMinutes / MINUTES_IN_HOUR),
            unit: 'hours' as TimeUnit,
        }
    }

    if (isWhole(totalMinutes)) {
        return {
            inputValue: formatNumber(totalMinutes),
            unit: 'minutes' as TimeUnit,
        }
    }

    return {
        inputValue: formatNumber(spendTime),
        unit: 'hours' as TimeUnit,
    }
}

export default function TimeTrackerPicker({ getSpendTime, spendTime, onlyIcon, dropdownPosition = 'right' }: TimeTrackerPickerProps) {
    const [inputValue, setInputValue] = useState('')
    const [timeUnit, setTimeUnit] = useState<TimeUnit>('hours')
    const [isUnitDropdownOpen, setIsUnitDropdownOpen] = useState(false)
    const [hasUnitSelection, setHasUnitSelection] = useState(false)
    const [isPickerOpen, setIsPickerOpen] = useState(false)
    const timeUnitRef = useRef<TimeUnit>('hours')
    const hasPendingChangeRef = useRef(false)

    useEffect(() => {
        timeUnitRef.current = timeUnit
    }, [timeUnit])

    useEffect(() => {
        hasPendingChangeRef.current = false

        if (spendTime == null || Number.isNaN(Number(spendTime))) {
            setInputValue('')
            setTimeUnit('hours')
            setHasUnitSelection(false)
            return
        }

        const nextState = getDisplayState(Number(spendTime), timeUnitRef.current)
        setInputValue(nextState.inputValue)
        setTimeUnit(nextState.unit)
        setHasUnitSelection(true)
    }, [spendTime])

    const selectedUnitTitle = useMemo(() => {
        return TIME_UNIT_OPTIONS.find((item) => item.id === timeUnit)?.title || 'Единица времени'
    }, [timeUnit])

    const unitLabel = hasUnitSelection ? selectedUnitTitle : 'Единица времени'

    const applyValue = (nextRawValue = inputValue, nextUnit = timeUnit) => {
        if (!hasPendingChangeRef.current) {
            return
        }

        const trimmedValue = nextRawValue.trim()

        if (!trimmedValue) {
            hasPendingChangeRef.current = false

            if (spendTime == null || Number.isNaN(Number(spendTime))) {
                setInputValue('')
                setTimeUnit(nextUnit)
                return
            }

            const nextState = getDisplayState(Number(spendTime), nextUnit)
            setInputValue(nextState.inputValue)
            setTimeUnit(nextState.unit)
            return
        }

        const parsedValue = Number(trimmedValue.replace(',', '.'))

        if (Number.isNaN(parsedValue) || parsedValue < 0) {
            return
        }

        const normalizedValue = roundTo(parsedValue)
        const totalHours = roundTo(toHours(normalizedValue, nextUnit))

        setInputValue(formatNumber(normalizedValue))
        setTimeUnit(nextUnit)
        setHasUnitSelection(true)
        hasPendingChangeRef.current = false

        const currentMinutes = spendTime == null || Number.isNaN(Number(spendTime))
            ? 0
            : Math.round(Number(spendTime) * MINUTES_IN_HOUR)
        const nextMinutes = Math.round(totalHours * MINUTES_IN_HOUR)

        if (currentMinutes !== nextMinutes) {
            getSpendTime(totalHours)
        }
    }

    const setTimeUnitHandler = (nextUnit: TimeUnit) => {
        setIsUnitDropdownOpen(false)
        setHasUnitSelection(true)
        hasPendingChangeRef.current = true
        applyValue(inputValue, nextUnit)
    }

    const setPickerOpenHandler = (nextIsOpen: boolean) => {
        if (isPickerOpen && !nextIsOpen) {
            applyValue()
            setIsUnitDropdownOpen(false)
        }

        setIsPickerOpen(nextIsOpen)
    }

    const trigger = (isOpen: boolean) => onlyIcon ? (
        <TimeIcon color={(isOpen || !!spendTime) ? '#64617E' : '#B1AEC7'} />
    ) : (
        <Plate title={spendTime ? `${spendTime}г.` : "Учет времени"}
               icon={<TimeIcon color={(isOpen || !!spendTime) ? '#64617E' : '#B1AEC7'} />}
               active={isOpen}
               filled={!!spendTime}/>
    )

    return (
        <div className={styles.timeTrackerPicker}>
            <DropDown position={dropdownPosition} btn={trigger} isOpen={isPickerOpen} setIsOpen={setPickerOpenHandler} contentClassName={styles.timeTrackerPicker__dropdownContent}>
                <div className={styles.timeTrackerPicker__content}>
                    <input
                        className={styles.timeTrackerPicker__input}
                        onFocus={() => setIsUnitDropdownOpen(false)}
                        onBlur={() => applyValue()}
                        onChange={(e) => {
                            setIsUnitDropdownOpen(false)
                            hasPendingChangeRef.current = true
                            setInputValue(e.target.value)
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                applyValue()
                                setIsUnitDropdownOpen(false)
                                setIsPickerOpen(false)
                            }
                        }}
                        value={inputValue}
                        type="text"
                        inputMode="decimal"
                        placeholder={"Затраченное время"}
                    />

                    <div className={styles.timeTrackerPicker__unitSelect}>
                        <button
                            type="button"
                            className={`${styles.timeTrackerPicker__unitTrigger} ${isUnitDropdownOpen ? styles.timeTrackerPicker__unitTriggerOpen : ''}`}
                            onClick={() => setIsUnitDropdownOpen((prev) => !prev)}
                        >
                            <span className={hasUnitSelection ? styles.timeTrackerPicker__unitTriggerValue : styles.timeTrackerPicker__unitTriggerPlaceholder}>
                                {unitLabel}
                            </span>
                            <svg
                                className={`${styles.timeTrackerPicker__unitTriggerArrow} ${isUnitDropdownOpen ? styles.timeTrackerPicker__unitTriggerArrowOpen : ''}`}
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M5 7.5L10 12.5L15 7.5"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>

                        {isUnitDropdownOpen && (
                            <div className={styles.timeTrackerPicker__unitDropdownContent}>
                                <div className={styles.timeTrackerPicker__unitMenu}>
                                    {TIME_UNIT_OPTIONS.map((item) => (
                                        <div
                                            key={item.id}
                                            className={`${styles.timeTrackerPicker__unitMenuItem} ${timeUnit === item.id ? styles.timeTrackerPicker__unitMenuItemActive : ''}`}
                                            onClick={() => setTimeUnitHandler(item.id)}
                                        >
                                            {item.title}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <Divider />
                    <div className={styles.timeTrackerPicker__info}>
                        <InfoIcon />
                        <p>
                            Данные сохраняются автоматически. Если время не совпало с реальным, измените его тут позже.
                        </p>
                    </div>
                </div>
            </DropDown>
        </div>
    )
}
